import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  Lock, CreditCard, ArrowLeft, ArrowRight, CheckCircle2, AlertCircle,
  Globe, Search, User, ShoppingBag, Loader2, Copy, Eye, EyeOff, KeyRound, ExternalLink, Server,
} from "lucide-react";
import Logo from "../components/Logo";
import { useCMS } from "../context/CMSContext";
import { checkDomain, createCheckoutOrder, verifyCheckout, getPublicTaxConfig, getPaymentLink, verifyPaymentLink } from "../lib/api";
import { trackBeginCheckout, trackPurchase } from "../lib/tracking";

const STEPS = [
  { id: "domain", label: "Domain", icon: Globe },
  { id: "details", label: "Your Details", icon: User },
  { id: "review", label: "Review & Pay", icon: ShoppingBag },
];

function CredRow({ label, value, isLink, hideToggle, mute }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const display = hideToggle || show ? (value || "") : "•".repeat(Math.max(8, (value || "").length));
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(value || ""); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* noop */ }
  };
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#11151C]/55">{label}</div>
      <div className="mt-1 flex items-center justify-between gap-2">
        {isLink ? (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`cred-link-${label.replace(/\s+/g, "-").toLowerCase()}`}
            className="inline-flex items-center gap-1 text-[#FF6B3D] font-semibold text-sm break-all"
          >
            {value} <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <code
            data-testid={`cred-value-${label.replace(/\s+/g, "-").toLowerCase()}`}
            className={`font-mono text-sm break-all ${mute ? "text-[#11151C]/45 italic" : "text-[#11151C]"}`}
          >
            {display || "—"}
          </code>
        )}
        {!mute && (
          <div className="flex items-center shrink-0">
            {!hideToggle && (
              <button
                type="button"
                onClick={() => setShow(!show)}
                title={show ? "Hide" : "Reveal"}
                className="p-1.5 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70"
              >
                {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            )}
            <button
              type="button"
              onClick={onCopy}
              disabled={!value}
              title={`Copy ${label}`}
              data-testid={`cred-copy-${label.replace(/\s+/g, "-").toLowerCase()}`}
              className="ml-1 p-1.5 rounded-md hover:bg-[#FFE6D6] text-[#11151C]/70 disabled:opacity-30"
            >
              {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CredCard({ title, icon: Icon, items, footnote }) {
  return (
    <div className="lm-card p-4 bg-[#FFFBF6] border border-[#FF6B3D]/25" data-testid={`cred-card-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="font-bold text-[#11151C] inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-4 h-4 text-[#FF6B3D]" />}
        {title}
      </div>
      <div className="mt-3 space-y-3">
        {items.map((it, idx) => <CredRow key={idx} {...it} />)}
      </div>
      {footnote && <p className="text-[11px] text-[#11151C]/55 mt-3">{footnote}</p>}
    </div>
  );
}

function Stepper({ current }) {
  const idx = STEPS.findIndex((s) => s.id === current);
  return (
    <div className="flex items-center gap-2 sm:gap-4" data-testid="checkout-stepper">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const done = i < idx;
        const active = i === idx;
        return (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 ${active || done ? "text-[#11151C]" : "text-[#11151C]/35"}`}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs border-2 transition-colors ${
                  done
                    ? "bg-[#FF6B3D] border-[#FF6B3D] text-white"
                    : active
                    ? "bg-[#11151C] border-[#11151C] text-white"
                    : "bg-white border-[#11151C]/20"
                }`}
              >
                {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className="hidden sm:inline text-sm font-semibold">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 ${done ? "bg-[#FF6B3D]" : "bg-[#11151C]/10"}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function CheckoutPage() {
  const [params] = useSearchParams();
  const { cms } = useCMS();
  const allPlans = cms?.plans || [];
  // Resolve plan key from URL, fall back to the first plan in the CMS (so old links
  // like ?plan=spark gracefully degrade to whatever plan now exists instead of 404).
  const requestedKey = params.get("plan");
  const planKey = requestedKey || (allPlans[0]?.key ?? "");
  const plan = allPlans.find((p) => p.key === planKey);
  const symbol = cms?.site?.currencySymbol || "\u20b9";
  const navigate = useNavigate();

  // ---------- Pay-link mode (?token=...) ----------
  const linkToken = params.get("token");
  const [payLink, setPayLink] = useState(null);
  const [payLinkLoading, setPayLinkLoading] = useState(!!linkToken);
  const [payLinkError, setPayLinkError] = useState("");
  const payLinkMode = !!linkToken;

  const [step, setStep] = useState("domain"); // domain | details | review | processing | success | error
  const [domainQuery, setDomainQuery] = useState("");
  const [domainResult, setDomainResult] = useState(null); // {query, exists, can_proceed, message, package}
  const [domainChecking, setDomainChecking] = useState(false);
  const [domainError, setDomainError] = useState("");
  const [chosenDomain, setChosenDomain] = useState(null); // { name, action }

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_company: "",
    customer_address: "",
    customer_city: "",
    customer_postal: "",
    customer_country: "India",
  });
  const [error, setError] = useState("");
  const [orderInfo, setOrderInfo] = useState(null);
  const [provResult, setProvResult] = useState(null); // verifyCheckout response
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // Bootstrap pay-link mode: fetch link, prefill form, auto-jump to review
  useEffect(() => {
    if (!linkToken) return;
    (async () => {
      try {
        const link = await getPaymentLink(linkToken);
        setPayLink(link);
        if (link.status === "paid") {
          setPayLinkError("This payment link has already been paid. Please log in to your dashboard to access your hosting.");
        } else if (link.status === "cancelled") {
          setPayLinkError("This payment link was cancelled by the team. Please request a new one.");
        } else if (link.status === "expired") {
          setPayLinkError("This payment link has expired. We've sent (or will send) a fresh one to your email.");
        } else {
          setForm((f) => ({
            ...f,
            customer_name: link.customer_name || "",
            customer_email: link.customer_email || "",
            customer_phone: link.customer_phone || "",
          }));
          setChosenDomain({ name: link.domain_name, action: "paylink" });
          setStep("review");
        }
      } catch (e) {
        setPayLinkError(e?.response?.data?.detail || "Could not load this payment link.");
      } finally {
        setPayLinkLoading(false);
      }
    })();
  }, [linkToken]);

  const isValidDomain = (s) =>
    /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z]{2,})+$/i.test((s || "").trim());

  const handleDomainCheck = async (e) => {
    e?.preventDefault?.();
    setDomainError("");
    setDomainResult(null);
    const q = domainQuery.trim().toLowerCase();
    if (!isValidDomain(q)) {
      setDomainError("Enter a valid domain (e.g. yourbusiness.com)");
      return;
    }
    setDomainChecking(true);
    try {
      const data = await checkDomain(q);
      setDomainResult(data);
    } catch (err) {
      setDomainError(err?.response?.data?.detail || "Domain check failed. Please try again.");
    }
    setDomainChecking(false);
  };

  const continueWithDomain = () => {
    if (!domainResult?.can_proceed) return;
    setChosenDomain({ name: domainResult.query, action: "use_existing" });
    setStep("details");
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submitDetails = (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      setError("Name, email and phone are required");
      return;
    }
    setError("");
    setStep("review");
  };

  const startPayment = async () => {
    setError("");
    setProcessing(true);
    try {
      // ----- Pay-link mode: skip create-order, use the link's existing razorpay order -----
      if (payLinkMode && payLink) {
        if (!payLink.razorpay_order_id) {
          setError("This payment link is missing a Razorpay order. Please ask the team to resend it.");
          setProcessing(false);
          return;
        }
        setOrderInfo({
          plan: { name: payLink.plan_name },
          razorpay_configured: true,
        });
        const opts = {
          key: payLink.razorpay_key_id,
          amount: payLink.amount_paise,
          currency: payLink.currency,
          name: cms?.site?.brandName || "Live Mover",
          description: `${payLink.plan_name} · ${payLink.domain_name}`,
          order_id: payLink.razorpay_order_id,
          prefill: {
            name: form.customer_name,
            email: form.customer_email,
            contact: form.customer_phone,
          },
          theme: { color: "#FF6B3D" },
          handler: async (resp) => {
            try {
              const result = await verifyPaymentLink(linkToken, {
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_signature: resp.razorpay_signature,
                order_id: payLink.token, // not used by backend, but model requires it
              });
              setProvResult(result);
              try {
                trackPurchase({
                  transaction_id: resp.razorpay_payment_id,
                  plan_key: payLink.plan_key,
                  plan_name: payLink.plan_name,
                  value: Number(payLink.total_inr) || Number(payLink.amount_inr) || 0,
                  currency: payLink.currency || "INR",
                  months: payLink.billing_months,
                });
              } catch (_) { /* never let tracking break success */ }
              setStep("success");
            } catch (e2) {
              setError(e2?.response?.data?.detail || "Payment verification failed");
              setStep("error");
            } finally {
              setProcessing(false);
            }
          },
          modal: {
            ondismiss: () => setProcessing(false),
          },
        };
        if (window.Razorpay) {
          try {
            trackBeginCheckout({
              plan_key: payLink.plan_key,
              plan_name: payLink.plan_name,
              price: payLink.amount_inr,
              value: payLink.total_inr || payLink.amount_inr,
              currency: payLink.currency || "INR",
              months: payLink.billing_months,
            });
          } catch (_) { /* ignore */ }
          new window.Razorpay(opts).open();
        } else {
          setError("Razorpay script not loaded. Please refresh.");
          setProcessing(false);
        }
        return;
      }

      // ----- Standard flow -----
      const data = await createCheckoutOrder({
        plan_id: planKey,
        domain_name: chosenDomain?.name,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        billing_months: Number(months),
      });
      setOrderInfo(data);

      if (!data.razorpay_configured) {
        setError("Payment gateway not yet configured. Your order has been received — our team will contact you.");
        setStep("success");
        setProcessing(false);
        return;
      }
      const opts = {
        key: data.razorpay_key_id,
        amount: data.amount_paise,
        currency: data.currency,
        name: cms?.site?.brandName || "Live Mover",
        description: `${data.plan?.name || planKey} · ${chosenDomain?.name}`,
        order_id: data.razorpay_order?.id,
        prefill: {
          name: form.customer_name,
          email: form.customer_email,
          contact: form.customer_phone,
        },
        theme: { color: "#FF6B3D" },
        handler: async (resp) => {
          try {
            const result = await verifyCheckout({
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_signature: resp.razorpay_signature,
              order_id: data.order_id,
            });
            setProvResult(result);
            try {
              trackPurchase({
                transaction_id: resp.razorpay_payment_id,
                plan_key: planKey,
                plan_name: plan?.name,
                value: Number(data.total_inr) || Number(breakdown.total) || planPrice,
                currency: "INR",
                months: Number(months) || 12,
              });
            } catch (_) { /* ignore */ }
            setStep("success");
          } catch (e2) {
            setError(e2?.response?.data?.detail || "Payment verification failed");
            setStep("error");
          }
          setProcessing(false);
        },
        modal: { ondismiss: () => setProcessing(false) },
      };
      try {
        trackBeginCheckout({
          plan_key: planKey,
          plan_name: plan?.name,
          price: planPrice,
          value: Number(data.total_inr) || Number(breakdown.total) || planPrice,
          currency: "INR",
          months: Number(months) || 12,
        });
      } catch (_) { /* ignore */ }
      const rzp = new window.Razorpay(opts);
      rzp.open();
    } catch (e2) {
      setError(e2?.response?.data?.detail || "Could not create order");
      setProcessing(false);
      setStep("error");
    }
  };

  const [months, setMonths] = useState(12);
  // In pay-link mode the months are fixed by the link
  useEffect(() => {
    if (payLink?.billing_months) setMonths(Number(payLink.billing_months));
  }, [payLink]);
  const tiers = useMemo(() => {
    const t = plan?.pricing_tiers || [];
    if (t.length) return t;
    // legacy fallback: single 12-month tier from plan.price
    return [{ months: 12, price: parseFloat(plan?.price || 0) || 0, twentyiTypeId: plan?.twentyiTypeId }];
  }, [plan]);

  const activeTier = useMemo(() => tiers.find((t) => Number(t.months) === Number(months)) || tiers[0], [tiers, months]);

  const planPrice = useMemo(() => {
    if (payLinkMode && payLink) return Number(payLink.amount_inr) || 0;
    return parseFloat(activeTier?.price || plan?.price || 0) || 0;
  }, [activeTier, plan, payLinkMode, payLink]);

  const [tax, setTax] = useState({ gst_enabled: false, gst_rate: 0, gst_mode: "IGST" });
  useEffect(() => {
    getPublicTaxConfig().then(setTax).catch(() => {});
  }, []);

  const breakdown = useMemo(() => {
    if (payLinkMode && payLink) {
      const sub = Number(payLink.amount_inr) || 0;
      const total = Number(payLink.total_inr) || sub;
      const gstAmount = +(total - sub).toFixed(2);
      const perMonth = +(total / Math.max(1, Number(payLink.billing_months || 12))).toFixed(2);
      return { sub, gstAmount, total, perMonth };
    }
    const sub = planPrice;
    const gstAmount = tax.gst_enabled ? +(sub * (tax.gst_rate || 0) / 100).toFixed(2) : 0;
    const total = +(sub + gstAmount).toFixed(2);
    const perMonth = +(total / Math.max(1, Number(months))).toFixed(2);
    return { sub, gstAmount, total, perMonth };
  }, [planPrice, tax, months, payLinkMode, payLink]);

  if (payLinkMode && payLinkLoading) {
    return (
      <div className="lm-font min-h-screen flex items-center justify-center bg-[#FFFBF6]">
        <div className="inline-flex items-center gap-2 text-[#11151C]/70 text-sm">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading your payment link…
        </div>
      </div>
    );
  }

  if (payLinkMode && payLinkError) {
    return (
      <div className="lm-font min-h-screen flex items-center justify-center bg-[#FFFBF6] p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-10 h-10 text-[#B23B12] mx-auto mb-3" />
          <p className="text-[#11151C] font-bold text-lg">Payment link issue</p>
          <p className="text-[#11151C]/65 text-sm mt-2">{payLinkError}</p>
          <Link to="/" className="lm-btn-primary mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (!plan && !payLinkMode) {
    return (
      <div className="lm-font min-h-screen flex items-center justify-center bg-[#FFFBF6] p-4">
        <div className="text-center max-w-md">
          <p className="text-[#11151C] font-bold text-lg">
            We couldn&apos;t find a plan called <code className="text-[#B23B12]">{planKey || "(none)"}</code>
          </p>
          <p className="text-[#11151C]/65 text-sm mt-2">
            Our plan names have been updated. Pick from our current line-up below:
          </p>
          {allPlans.length > 0 && (
            <div className="mt-4 grid gap-2">
              {allPlans.map((p) => (
                <Link
                  key={p.key}
                  to={`/checkout?plan=${p.key}`}
                  data-testid={`checkout-fallback-plan-${p.key}`}
                  className="inline-flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-[#FF6B3D]/30 bg-white hover:bg-[#FFE6D6] text-left"
                >
                  <span className="font-bold text-[#11151C]">{p.name}</span>
                  <span className="text-xs text-[#B23B12] font-mono">{p.key}</span>
                </Link>
              ))}
            </div>
          )}
          <Link to="/" className="lm-btn-primary mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="lm-font min-h-screen lm-hero-bg" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <Logo size={36} />
          <span className="lm-font font-extrabold text-[#11151C] text-lg">
            {cms?.site?.brandName || "Live Mover"}
          </span>
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-4 pb-16">
        <h1 className="lm-font font-extrabold text-[#11151C] text-3xl sm:text-4xl mt-2">Complete your order</h1>
        <p className="text-[#11151C]/65 mt-1 text-sm">Secure checkout · INR · Razorpay</p>

        <div className="mt-6">
          <Stepper current={step === "success" || step === "error" ? "review" : step} />
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 mt-8">
          <div className="bg-white rounded-2xl border border-[#11151C]/10 p-5 sm:p-7">
            {/* STEP 1: DOMAIN */}
            {step === "domain" && (
              <div data-testid="step-domain">
                <h3 className="lm-font font-extrabold text-[#11151C] text-xl">Your domain</h3>
                <p className="text-[#11151C]/65 text-sm mt-1">
                  Enter the domain you&apos;ll use for this hosting. We&apos;ll make sure no other account is already using it.
                </p>

                <form onSubmit={handleDomainCheck} className="mt-5 flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-[#11151C]/40 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      value={domainQuery}
                      onChange={(e) => setDomainQuery(e.target.value)}
                      placeholder="yourdomain.com"
                      data-testid="domain-input"
                      className="w-full pl-9 pr-3 py-3 border border-[#11151C]/15 rounded-xl text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={domainChecking}
                    data-testid="domain-check-btn"
                    className="lm-btn-primary px-5 py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {domainChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    {domainChecking ? "Checking…" : "Check"}
                  </button>
                </form>

                {domainError && (
                  <div className="mt-3 flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {domainError}
                  </div>
                )}

                {domainResult && (
                  <div
                    className={`mt-4 rounded-xl px-4 py-4 border ${
                      domainResult.can_proceed
                        ? "bg-[#FFFBF6] border-[#FF6B3D]/30"
                        : "bg-red-50 border-red-200"
                    }`}
                    data-testid="domain-result"
                  >
                    <div className="flex items-start gap-3">
                      {domainResult.can_proceed ? (
                        <CheckCircle2 className="w-5 h-5 text-[#FF6B3D] flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="min-w-0">
                        <div className="font-bold text-[#11151C] break-all">{domainResult.query}</div>
                        <div className={`text-xs mt-1 ${domainResult.can_proceed ? "text-[#B23B12]" : "text-red-700"}`}>
                          {domainResult.message}
                        </div>
                        {domainResult.exists && domainResult.package && (
                          <div className="mt-2 text-[11px] text-[#11151C]/60">
                            Existing: <code className="bg-white px-1.5 py-0.5 rounded">{domainResult.package.name || domainResult.package.id}</code>
                          </div>
                        )}
                        {!domainResult.can_proceed && (
                          <p className="mt-3 text-xs text-[#11151C]/70 leading-relaxed">
                            If this is your domain or you need help, please <a href="#" onClick={(e) => { e.preventDefault(); if (window.Tawk_API?.maximize) window.Tawk_API.maximize(); }} className="font-semibold text-[#FF6B3D] hover:underline">contact our support team</a> — we&apos;ll help you sort it out.
                          </p>
                        )}
                      </div>
                    </div>

                    {domainResult.can_proceed && (
                      <button
                        onClick={continueWithDomain}
                        data-testid="domain-continue-btn"
                        className="lm-btn-primary mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
                      >
                        Continue with {domainResult.query} <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}

                <p className="mt-5 text-xs text-[#11151C]/55">
                  <strong>Note:</strong> We don&apos;t register or transfer domains here. Bring your own domain — once your hosting is active, point its DNS to Live Mover.
                </p>
              </div>
            )}

            {/* STEP 2: DETAILS */}
            {step === "details" && (
              <form onSubmit={submitDetails} className="space-y-4" data-testid="step-details">
                <div className="flex items-center justify-between">
                  <h3 className="lm-font font-extrabold text-[#11151C] text-xl">Your details</h3>
                  <button
                    type="button"
                    onClick={() => setStep("domain")}
                    className="text-xs font-semibold text-[#11151C]/60 hover:text-[#11151C] inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-3 h-3" /> Change domain
                  </button>
                </div>

                <div className="text-xs bg-[#FFFBF6] border border-[#FF6B3D]/30 text-[#B23B12] rounded-lg px-3 py-2 inline-flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5" /> Domain: <strong>{chosenDomain?.name}</strong>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#11151C]">Full name *</label>
                  <input
                    name="customer_name" required value={form.customer_name} onChange={handleChange}
                    data-testid="customer-name"
                    className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-semibold text-[#11151C]">Email *</label>
                    <input
                      type="email" name="customer_email" required value={form.customer_email} onChange={handleChange}
                      data-testid="customer-email"
                      className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#11151C]">Phone *</label>
                    <input
                      name="customer_phone" required value={form.customer_phone} onChange={handleChange}
                      data-testid="customer-phone"
                      className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#11151C]">Company (optional)</label>
                  <input
                    name="customer_company" value={form.customer_company} onChange={handleChange}
                    data-testid="customer-company"
                    className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-3 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20"
                  />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#11151C]">Address (optional)</label>
                  <input
                    name="customer_address" value={form.customer_address} onChange={handleChange}
                    className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-3 text-sm"
                  />
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm font-semibold text-[#11151C]">City</label>
                    <input
                      name="customer_city" value={form.customer_city} onChange={handleChange}
                      className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#11151C]">Postal Code</label>
                    <input
                      name="customer_postal" value={form.customer_postal} onChange={handleChange}
                      className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-3 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-[#11151C]">Country</label>
                    <input
                      name="customer_country" value={form.customer_country} onChange={handleChange}
                      className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-3 text-sm"
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="continue-to-review"
                  className="lm-btn-primary w-full py-3.5 rounded-xl font-bold text-sm inline-flex items-center justify-center gap-2"
                >
                  Continue to review <ArrowRight className="w-4 h-4" />
                </button>
              </form>
            )}

            {/* STEP 3: REVIEW + PAY */}
            {step === "review" && (
              <div data-testid="step-review">
                <div className="flex items-center justify-between">
                  <h3 className="lm-font font-extrabold text-[#11151C] text-xl">Review & pay</h3>
                  <button
                    type="button"
                    onClick={() => setStep("details")}
                    className="text-xs font-semibold text-[#11151C]/60 hover:text-[#11151C] inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-3 h-3" /> Edit details
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  <RowReview label="Plan" value={plan.name} />
                  <RowReview label="Domain" value={chosenDomain?.name} />
                  <RowReview label="Name" value={form.customer_name} />
                  <RowReview label="Email" value={form.customer_email} />
                  <RowReview label="Phone" value={form.customer_phone} />
                  {form.customer_company && <RowReview label="Company" value={form.customer_company} />}
                  {(form.customer_city || form.customer_country) && (
                    <RowReview label="Location" value={`${form.customer_city || ""}${form.customer_city && form.customer_country ? ", " : ""}${form.customer_country || ""}`} />
                  )}
                </div>

                {error && (
                  <div className="mt-4 flex items-center gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded-lg p-3">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                  </div>
                )}

                <button
                  onClick={startPayment}
                  disabled={processing}
                  data-testid="pay-now-btn"
                  className="lm-btn-primary w-full mt-6 py-3.5 rounded-xl font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4 text-[#FF6B3D]" />}
                  {processing ? "Opening Razorpay…" : `Pay ${symbol}${breakdown.total.toLocaleString("en-IN")}`}
                </button>
                <div className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-[#11151C]/50">
                  <Lock className="w-3 h-3" /> Secure 256-bit SSL · Razorpay
                </div>
              </div>
            )}

            {/* SUCCESS */}
            {step === "success" && (
              <div className="py-6" data-testid="step-success">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#FFE6D6] flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-9 h-9 text-[#FF6B3D]" />
                  </div>
                  <h3 className="lm-font font-extrabold text-[#11151C] text-2xl mt-4">
                    {provResult ? "Your hosting is ready!" : "Order received"}
                  </h3>
                  <p className="text-[#11151C]/70 mt-2 text-sm">
                    {provResult
                      ? `${provResult.domain_name || ""} has been provisioned. Save your credentials below — also emailed to ${provResult.customer_email}.`
                      : (error || "Your hosting is being provisioned. You'll receive control panel login details via email shortly.")}
                  </p>
                  <p className="text-[#11151C]/50 text-xs mt-2">Order ID: {orderInfo?.order_id}</p>
                </div>

                {provResult && (
                  <div className="mt-6 space-y-4">
                    {/* Portal account */}
                    {provResult.portal_login?.password && (
                      <CredCard
                        title="My Account portal"
                        icon={User}
                        items={[
                          { label: "Login URL", value: window.location.origin + "/login", isLink: true, hideToggle: true },
                          { label: "Email", value: provResult.portal_login.email, hideToggle: true },
                          { label: "Password", value: provResult.portal_login.password },
                        ]}
                        footnote="Use this to log in to your Live Mover account — view invoices, manage hosting and credentials."
                      />
                    )}
                    {provResult.portal_login && !provResult.portal_login.password && (
                      <div className="lm-card p-4 bg-[#FFFBF6] border border-[#FF6B3D]/20 text-sm" data-testid="portal-existing-note">
                        <div className="font-bold text-[#11151C] inline-flex items-center gap-1.5">
                          <User className="w-4 h-4 text-[#FF6B3D]" /> My Account portal
                        </div>
                        <p className="text-[#11151C]/65 text-xs mt-1">
                          You already have a Live Mover account ({provResult.portal_login.email}). Log in with your existing password.
                        </p>
                      </div>
                    )}

                    {/* Control panel */}
                    {provResult.control_panel && (
                      <CredCard
                        title="Hosting Control Panel"
                        icon={Server}
                        items={[
                          { label: "Login URL", value: provResult.control_panel.url, isLink: true, hideToggle: true },
                          { label: "Username", value: provResult.control_panel.username, hideToggle: true },
                          provResult.control_panel.password
                            ? { label: "Password", value: provResult.control_panel.password }
                            : { label: "Password", value: "(set via My Account portal)", hideToggle: true, mute: true },
                        ]}
                        footnote="Use this to manage your hosting files, email, databases and SSL on the control panel."
                      />
                    )}
                  </div>
                )}

                <div className="text-center mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                  <Link to="/portal" className="lm-btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold">
                    Go to My Account <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold border border-[#11151C]/15 hover:bg-[#FFE6D6]/40">
                    Back to home
                  </Link>
                </div>
              </div>
            )}

            {/* ERROR */}
            {step === "error" && (
              <div className="text-center py-8" data-testid="step-error">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                  <AlertCircle className="w-9 h-9 text-red-600" />
                </div>
                <h3 className="lm-font font-extrabold text-[#11151C] text-2xl mt-4">Something went wrong</h3>
                <p className="text-red-600 mt-2 text-sm">{error}</p>
                <button
                  onClick={() => { setStep("review"); setError(""); }}
                  className="lm-btn-primary mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-bold"
                >
                  Try again
                </button>
              </div>
            )}
          </div>

          {/* Order summary */}
          <div className="bg-white rounded-2xl border border-[#11151C]/10 p-5 sm:p-7 h-fit lg:sticky lg:top-6">
            <h3 className="lm-font font-extrabold text-[#11151C] text-lg">Order summary</h3>
            <div className="mt-4 pb-4 border-b border-[#11151C]/5">
              <div className="flex justify-between text-sm">
                <span className="text-[#11151C]/70">Plan</span>
                <span className="font-semibold text-[#11151C]">{plan.name}</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-[#11151C]/70">Billing</span>
                <span className="font-semibold text-[#11151C]">{months === 12 ? "1 year" : `${months / 12} years`}</span>
              </div>
              {chosenDomain && (
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-[#11151C]/70">Domain</span>
                  <span className="font-semibold text-[#11151C] truncate ml-2">{chosenDomain.name}</span>
                </div>
              )}
            </div>

            {tiers.length > 1 && (
              <div className="mt-4 pb-4 border-b border-[#11151C]/5">
                <div className="text-[11px] uppercase font-bold tracking-wider text-[#11151C]/65 mb-2">Choose duration</div>
                <div className="space-y-2" data-testid="duration-selector">
                  {tiers.map((t) => {
                    const m = Number(t.months);
                    const price = Number(t.price) || 0;
                    const per = m > 0 ? (price / m).toFixed(2) : "0";
                    const active = Number(months) === m;
                    return (
                      <label
                        key={m}
                        className={`flex items-center justify-between gap-3 p-3 rounded-xl cursor-pointer border transition-colors ${active ? "border-[#FF6B3D] bg-[#FFFBF6]" : "border-[#11151C]/10 hover:border-[#FF6B3D]/40"}`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="billing-months"
                            checked={active}
                            onChange={() => setMonths(m)}
                            data-testid={`tier-${m}`}
                            className="accent-[#FF6B3D]"
                          />
                          <span className="font-semibold text-[#11151C] text-sm">{m === 12 ? "1 year" : `${m / 12} years`}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-[#11151C] text-sm">{symbol}{price.toLocaleString("en-IN")}</div>
                          <div className="text-[11px] text-[#11151C]/55">{symbol}{Number(per).toLocaleString("en-IN", { maximumFractionDigits: 2 })} a month</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="mt-4 space-y-1.5">
              <div className="flex justify-between items-baseline">
                <span className="text-[#11151C]/70 text-sm">Subtotal</span>
                <span className="font-semibold text-[#11151C] text-sm">{symbol}{breakdown.sub.toLocaleString("en-IN")}</span>
              </div>
              {tax.gst_enabled && breakdown.gstAmount > 0 && (
                <div className="flex justify-between items-baseline" data-testid="checkout-gst-row">
                  <span className="text-[#11151C]/70 text-sm">{tax.gst_mode === "CGST_SGST" ? `CGST 9% + SGST 9%` : `IGST ${tax.gst_rate}%`}</span>
                  <span className="font-semibold text-[#11151C] text-sm">{symbol}{breakdown.gstAmount.toLocaleString("en-IN")}</span>
                </div>
              )}
              <div className="flex justify-between items-baseline pt-2 border-t border-[#11151C]/10">
                <span className="text-[#11151C]/70 text-sm">Total today</span>
                <span className="lm-font font-extrabold text-[#11151C] text-2xl" data-testid="checkout-total">
                  {symbol}{breakdown.total.toLocaleString("en-IN")}
                </span>
              </div>
              <p className="text-xs text-[#11151C]/55 text-right" data-testid="checkout-per-month">
                ≈ {symbol}{breakdown.perMonth.toLocaleString("en-IN", { maximumFractionDigits: 2 })} a month
              </p>
            </div>
            <p className="text-xs text-[#11151C]/50 mt-1 text-right">{plan.renew}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function RowReview({ label, value }) {
  return (
    <div className="flex justify-between items-start gap-3 text-sm">
      <span className="text-[#11151C]/55">{label}</span>
      <span className="font-semibold text-[#11151C] text-right break-words">{value || "—"}</span>
    </div>
  );
}

